import { ref, watch, type Ref } from 'vue'
import useQueryEncoder from '@/composables/useQueryEncoder'

// Global state for contract drawer
const showContractDrawer = ref(false)
const contractDrawerAction = ref<'show' | 'edit' | 'accept' | 'view'>('show')
const contractDrawerContractId = ref<string | undefined>(undefined)

const openAcceptContract = useQueryEncoder(
  'acceptcontract',
  false,
  (v: string | boolean | number) => `${v}` === '1' || v === 'true' || v === true,
)

export function useContractDrawer() {
  const openContractDrawer = (
    action: 'show' | 'edit' | 'accept' | 'view' = 'show',
    contractId?: string,
  ) => {
    contractDrawerAction.value = action
    contractDrawerContractId.value = action === 'view' ? contractId : undefined
    showContractDrawer.value = true
    if (action === 'accept') {
      openAcceptContract.value = true
    }
  }

  const closeContractDrawer = () => {
    showContractDrawer.value = false
  }

  watch(showContractDrawer, () => {
    openAcceptContract.value = showContractDrawer.value && contractDrawerAction.value === 'accept'
  })

  return {
    showContractDrawer,
    contractDrawerAction,
    contractDrawerContractId,
    openContractDrawer,
    closeContractDrawer,
  }
}

// Opens the drawer in accept mode from the ?acceptcontract=1 intent, but only
// once the hire query settles and it's ready — otherwise the page's blocking
// alerts stay visible.
export function useAcceptContractAutoOpen(loading: Ref<boolean>, canAccept: Ref<boolean>) {
  const { openContractDrawer } = useContractDrawer()
  let decided = false

  watch(
    loading,
    (isLoading) => {
      if (decided || isLoading) return
      decided = true

      if (openAcceptContract.value && canAccept.value) {
        openContractDrawer('accept')
      }
    },
    { immediate: true },
  )
}
